/*
 * GrindCTRL cart popup (Pull&Bear-style "Added to basket")
 * Self-contained: listens for the theme's ARMADA:CART:ADD event, opens a modal
 * showing the added item, cart summary, a discount progress bar, and
 * "You might like this" recommendations. Does not modify We Are Eight core files.
 */
(function () {
  'use strict';

  class GcCartPopup extends HTMLElement {
    constructor() {
      super();
      this.threshold = parseInt(this.dataset.threshold || '2999', 10) * 100; // cents
      this.moneyFormat = this.dataset.moneyFormat || 'LE {{amount}}';
      this.msgRemaining = this.dataset.msgRemaining || 'Spend [amount] more for 10% off';
      this.msgReached = this.dataset.msgReached || 'YOU UNLOCKED 10% OFF, DONT WORRY IT APPLIES AUTOMATICALLY AT CHECKOUT';
      this.promoCode = '';
      this.promoNote = '';
      this.overlay = this.querySelector('[data-gc-overlay]');
      this.panel = this.querySelector('[data-gc-panel]');
      this.body = this.querySelector('[data-gc-body]');
      this.lastAddedId = null;
      this._onKey = this._onKey.bind(this);
    }

    connectedCallback() {
      window.gcCartPopup = this;

      // Trigger 1: the theme's native add-to-cart event.
      document.addEventListener('ARMADA:CART:ADD', (e) => {
        try {
          const item = e.detail && e.detail.response && e.detail.response.items
            ? e.detail.response.items[0] : null;
          this.lastAddedId = item ? item.id : null;
        } catch (_) { this.lastAddedId = null; }
        this.trigger();
      });

      // Trigger 2: intercept /cart/add and /cart/(change|update|clear) network calls
      this.installNetworkHooks();

      this.addEventListener('click', (e) => {
        if (e.target.closest('[data-gc-close]')) { this.close(); return; }
        const addBtn = e.target.closest('[data-gc-add]');
        if (addBtn) { e.preventDefault(); this.addVariant(addBtn); }
      });
    }

    trigger() {
      if (this._triggerTimer) clearTimeout(this._triggerTimer);
      this._triggerTimer = setTimeout(() => this.open(), 60);
    }

    refreshIfOpen() {
      if (this._refreshTimer) clearTimeout(this._refreshTimer);
      this._refreshTimer = setTimeout(async () => {
        if (this.hidden && !this.classList.contains('is-open')) return;
        try {
          const cart = await fetch('/cart.js').then((r) => r.json());
          this.render(cart);
        } catch (_) {}
      }, 60);
    }

    installNetworkHooks() {
      const self = this;
      const isAddUrl = (url) => typeof url === 'string' && /\/cart\/add(\.js)?(\?|$)/.test(url);
      const isMutateUrl = (url) => typeof url === 'string' && /\/cart\/(change|update|clear)(\.js)?(\?|$)/.test(url);

      const origFetch = window.fetch;
      if (origFetch && !origFetch.__gcPatched) {
        const patched = function (input, init) {
          const url = typeof input === 'string' ? input : (input && input.url) || '';
          const method = ((init && init.method) || (input && input.method) || 'GET').toUpperCase();
          const p = origFetch.apply(this, arguments);
          if (method === 'POST' && !self._suppressHook) {
            if (isAddUrl(url)) {
              p.then((res) => { if (res && res.ok) { try { self.captureFromClone(res); } catch (_) {} self.trigger(); } }).catch(() => {});
            } else if (isMutateUrl(url)) {
              p.then((res) => { if (res && res.ok) { self.refreshIfOpen(); } }).catch(() => {});
            }
          }
          return p;
        };
        patched.__gcPatched = true;
        window.fetch = patched;
      }

      const origOpen = XMLHttpRequest.prototype.open;
      const origSend = XMLHttpRequest.prototype.send;
      if (!XMLHttpRequest.prototype.open.__gcPatched) {
        XMLHttpRequest.prototype.open = function (method, url) {
          const isPost = String(method).toUpperCase() === 'POST';
          this.__gcAdd = isPost && isAddUrl(url);
          this.__gcMutate = isPost && isMutateUrl(url);
          return origOpen.apply(this, arguments);
        };
        XMLHttpRequest.prototype.open.__gcPatched = true;
        XMLHttpRequest.prototype.send = function () {
          if ((this.__gcAdd || this.__gcMutate) && !self._suppressHook) {
            this.addEventListener('load', () => {
              if (this.status >= 200 && this.status < 300) {
                if (this.__gcAdd) {
                  try {
                    const data = JSON.parse(this.responseText);
                    const item = (data.items && data.items[0]) || (data.id ? data : null);
                    if (item) self.lastAddedId = item.id;
                  } catch (_) {}
                  self.trigger();
                } else if (this.__gcMutate) {
                  self.refreshIfOpen();
                }
              }
            });
          }
          return origSend.apply(this, arguments);
        };
      }
    }

    async captureFromClone(res) {
      const data = await res.clone().json();
      const item = (data.items && data.items[0]) || (data.id ? data : null);
      if (item) this.lastAddedId = item.id;
    }

    _onKey(e) { if (e.key === 'Escape') this.close(); }

    formatMoney(cents) {
      const value = (cents / 100).toFixed(2);
      const parts = value.split('.');
      const intPart = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      const withDecimals = intPart + '.' + parts[1];
      const noDecimals = parts[0].replace(/\B(?=(\d{3})+(?!\d))/g, ',');
      return this.moneyFormat
        .replace(/\{\{\s*amount\s*\}\}/g, withDecimals)
        .replace(/\{\{\s*amount_no_decimals\s*\}\}/g, noDecimals);
    }

    async open(prefetchedCart) {
      document.addEventListener('keydown', this._onKey);
      this.hidden = false;
      requestAnimationFrame(() => this.classList.add('is-open'));
      document.documentElement.style.overflow = 'hidden';
      if (!this.body.innerHTML.trim()) {
        this.body.innerHTML = '<div class="gc-cp__loading">…</div>';
      }
      try {
        const cart = prefetchedCart || await fetch('/cart.js').then((r) => r.json());
        this.render(cart);
        this.loadRecommendations(cart);
      } catch (_) {
        this.body.innerHTML = '<p class="gc-cp__err">Could not load your cart.</p>';
      }
    }

    close() {
      this.classList.remove('is-open');
      document.removeEventListener('keydown', this._onKey);
      document.documentElement.style.overflow = '';
      setTimeout(() => { this.hidden = true; }, 250);
    }

    shippingBarHTML(total) {
      const safeTotal = Math.max(Number(total) || 0, 0);
      const remaining = Math.max(this.threshold - safeTotal, 0);
      const pct = this.threshold > 0 ? Math.min((safeTotal / this.threshold) * 100, 100) : 0;
      const reached = safeTotal >= this.threshold;
      const text = reached
        ? this.msgReached
        : this.msgRemaining.replace('[amount]', this.formatMoney(remaining));
      return (
        '<div class="gc-cp__ship" role="status" aria-live="polite">' +
          '<p class="gc-cp__ship-text">' + text + '</p>' +
          '<div class="gc-cp__track"><div class="gc-cp__fill" style="inline-size:' + pct + '%"></div></div>' +
        '</div>'
      );
    }

    promoHTML() {
      return '';
    }

    render(cart) {
      const preDiscountTotal = cart && typeof cart.original_total_price === 'number'
        ? cart.original_total_price
        : (cart && cart.total_price) || 0;

      if (!cart || cart.item_count === 0) {
        this.body.innerHTML =
          '<div class="gc-cp__head">' +
            '<p class="gc-cp__title">Added to basket</p>' +
            '<button type="button" class="gc-cp__x" data-gc-close aria-label="Close">&times;</button>' +
          '</div>' +
          this.shippingBarHTML(0) +
          '<div class="gc-cp__empty">' +
            '<p>Your basket is empty.</p>' +
            '<a class="gc-cp__btn gc-cp__btn--primary" href="/collections/all">Continue shopping</a>' +
          '</div>';
        return;
      }

      const added = cart.items.find((i) => i.id === this.lastAddedId) || cart.items[0];
      const itemHTML =
        '<div class="gc-cp__item">' +
          (added.featured_image && added.featured_image.url
            ? '<img class="gc-cp__item-img" src="' + added.featured_image.url + '&width=160" alt="" loading="lazy">' : '') +
          '<div class="gc-cp__item-info">' +
            '<p class="gc-cp__item-title">' + added.product_title + '</p>' +
            (added.variant_title ? '<p class="gc-cp__item-variant">' + added.variant_title + '</p>' : '') +
            '<p class="gc-cp__item-price">' + this.formatMoney(added.original_line_price || added.final_line_price) + '</p>' +
          '</div>' +
        '</div>';

      this.body.innerHTML =
        '<div class="gc-cp__head">' +
          '<p class="gc-cp__title">Added to basket</p>' +
          '<button type="button" class="gc-cp__x" data-gc-close aria-label="Close">&times;</button>' +
        '</div>' +
        this.shippingBarHTML(preDiscountTotal) +
        itemHTML +
        '<div class="gc-cp__actions">' +
          '<a class="gc-cp__btn gc-cp__btn--ghost" href="/cart">See basket (' + cart.item_count + ')</a>' +
          '<a class="gc-cp__btn gc-cp__btn--primary" href="/checkout">Checkout</a>' +
        '</div>' +
        '<div class="gc-cp__recs" data-gc-recs hidden>' +
          '<p class="gc-cp__recs-title">You might like this</p>' +
          '<div class="gc-cp__recs-row" data-gc-recs-row></div>' +
        '</div>';
    }

    async loadRecommendations(cart) {
      if (!cart || !cart.items || !cart.items.length) return;
      const recsWrap = this.querySelector('[data-gc-recs]');
      const row = this.querySelector('[data-gc-recs-row]');
      if (!recsWrap || !row) return;
      const seedId = (cart.items[0] && cart.items[0].product_id) || null;
      if (!seedId) return;
      try {
        const data = await fetch('/recommendations/products.json?product_id=' + seedId + '&limit=6&intent=related')
          .then((r) => r.json());
        const inCart = new Set(cart.items.map((i) => i.product_id));
        const products = (data.products || []).filter((p) => !inCart.has(p.id)).slice(0, 6);
        if (!products.length) return;
        row.innerHTML = products.map((p) => {
          const v = p.variants && p.variants[0];
          const single = p.variants && p.variants.length === 1 && v && v.available;
          const img = p.featured_image || (p.images && p.images[0]) || '';
          const price = this.formatMoney(p.price);
          return (
            '<div class="gc-cp__card">' +
              '<a href="' + p.url + '" class="gc-cp__card-link">' +
                (img ? '<img class="gc-cp__card-img" src="' + img + '&width=200" alt="" loading="lazy">' : '') +
                '<p class="gc-cp__card-title">' + p.title + '</p>' +
                '<p class="gc-cp__card-price">' + price + '</p>' +
              '</a>' +
              (single
                ? '<button type="button" class="gc-cp__card-add" data-gc-add data-variant-id="' + v.id + '">+ Add</button>'
                : '<a class="gc-cp__card-add gc-cp__card-add--view" href="' + p.url + '">View product</a>') +
            '</div>'
          );
        }).join('');
        recsWrap.hidden = false;
      } catch (_) {}
    }

    async addVariant(btn) {
      const id = btn.dataset.variantId;
      if (!id) return;
      const original = btn.textContent;
      btn.disabled = true; btn.textContent = '…';
      this._suppressHook = true;
      try {
        await fetch('/cart/add.js', {
          method: 'POST',
          headers: { 'Content-Type': 'application/json' },
          body: JSON.stringify({ items: [{ id: Number(id), quantity: 1 }] })
        }).then((r) => r.json());
        const cart = await fetch('/cart.js').then((r) => r.json());
        this.updateCartCount(cart.item_count);
        this.render(cart);
        this.loadRecommendations(cart);
      } catch (_) {
        btn.disabled = false; btn.textContent = original;
      } finally {
        setTimeout(() => { this._suppressHook = false; }, 300);
      }
    }

    updateCartCount(count) {
      document.querySelectorAll('[data-cart-count], .cart-count, [data-armada-selector="cart-count"]').forEach((el) => {
        el.textContent = count;
      });
      document.dispatchEvent(new CustomEvent('ARMADA:CART:REFRESH'));
    }
  }

  if (!customElements.get('gc-cart-popup')) {
    customElements.define('gc-cart-popup', GcCartPopup);
  }
})();
